import type { ListedGroup, ListedPerson, SystemGroup } from "../../../api/access/accessModel";

/**
 * Moving a group in its scope's list (accounts.md GS-3): one drag, or "Move above…" from the
 * keyboard (PV3-3). Administrators stay on top and Everyone last; a group inside another
 * moves with it and stays among its siblings; only groups the viewer manages move, and only
 * among each other. The server checks the whole new order against its revision again.
 */

const PINNED: ReadonlySet<SystemGroup> = new Set(["administrators", "project_administrators", "everyone"]);

export function isPinned(group: ListedGroup): boolean {
  return group.system !== null && PINNED.has(group.system);
}

/** Whether the viewer may drag the group or move it from its menu. */
export function isMovable(group: ListedGroup): boolean {
  return group.manageable && !isPinned(group);
}

/** How deep the group sits inside other groups of the list (0: at the top level). */
export function depthOf(group: ListedGroup, groups: readonly ListedGroup[]): number {
  const byId = new Map(groups.map((listed) => [listed.groupId, listed]));
  let depth = 0;
  for (let parent = group.parentId === null ? undefined : byId.get(group.parentId); parent !== undefined; parent = parent.parentId === null ? undefined : byId.get(parent.parentId)) {
    depth += 1;
  }
  return depth;
}

/** The groups `moving` may be put directly above: movable siblings other than itself and the one already below it. */
export function moveTargets(groups: readonly ListedGroup[], movingId: string): readonly ListedGroup[] {
  const moving = groups.find((group) => group.groupId === movingId);
  if (moving === undefined || !isMovable(moving)) return [];
  const siblings = groups.filter((group) => group.parentId === moving.parentId);
  const next = siblings[siblings.indexOf(moving) + 1];
  return siblings.filter((group) => group !== moving && group !== next && isMovable(group));
}

/**
 * The list's ids with `movingId` and the groups inside it put directly above `targetId`, or
 * `null` when that isn't a move the viewer may make.
 */
export function moveAbove(groups: readonly ListedGroup[], movingId: string, targetId: string): readonly string[] | null {
  if (!moveTargets(groups, movingId).some((group) => group.groupId === targetId)) return null;
  const block = blockOf(groups, movingId);
  const rest = groups.map((group) => group.groupId).filter((groupId) => !block.includes(groupId));
  const at = rest.indexOf(targetId);
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}

/**
 * The current list's ids with `groupId` put back where it stood in `former`: directly above the
 * first group that followed it then and is still listed, or last when none is.
 */
export function restoredPosition(current: readonly string[], groupId: string, former: readonly string[]): readonly string[] {
  const rest = current.filter((id) => id !== groupId);
  const next = former.slice(former.indexOf(groupId) + 1).find((id) => rest.includes(id));
  const at = next === undefined ? rest.length : rest.indexOf(next);
  return [...rest.slice(0, at), groupId, ...rest.slice(at)];
}

/**
 * The groups inside `groupId` through which it reaches a person, down to their own, as the
 * server's `paths` say (GR-2, Sol critique C11); `null` for its own member or someone it doesn't
 * reach. Its members' list and the people list say "via" with them.
 */
export function viaGroups(people: readonly ListedPerson[], userId: string, groupId: string): readonly string[] | null {
  const path = people.find((person) => person.userId === userId)?.paths.find((chain) => chain[0] === groupId);
  return path === undefined || path.length < 2 ? null : path.slice(1);
}

/** The group's id and the ids of every group inside it, in list order. */
function blockOf(groups: readonly ListedGroup[], groupId: string): readonly string[] {
  const inside = new Set([groupId]);
  for (const group of groups) {
    if (group.parentId !== null && inside.has(group.parentId)) inside.add(group.groupId);
  }
  return groups.map((group) => group.groupId).filter((id) => inside.has(id));
}
